import { useState, useEffect, useMemo } from 'react';
import { useCartStorage } from '../../hooks/useCartStorage';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';
import {
  Box,
  Typography,
  Button,
  Alert,
  Grid,
  CircularProgress,
  Divider,
  TextField,
  Chip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ShoppingCart from '../../components/ShoppingCart';
import { productAPI } from '../../services/api';
import { filterPurchasableCartItems, isComingSoonCartItem } from '../../utils/cartUtils';

// Fallback shop images mapped by SKU — used only when a product has no
// admin-uploaded image. An uploaded image (Product Management) always wins.
const PRODUCT_IMAGE_MAP = {
  // 1.5mL equicenta CTM packshot (shop / cart)
  'eCTM-150': '/UnisynHub/Assets/equicenta-ctm-1.5ml.png',
  'EPL-1.5ML': '/UnisynHub/Assets/equicenta-ctm-1.5ml.png',
  'EQC-001': '/UnisynHub/Assets/equicenta-card.png',
  'MNT-001': '/UnisynHub/Assets/maintain-3ml-card.png',
  'MNT-002': '/UnisynHub/Assets/maintain-1.5ml-card.png',
};

// Fallback image if SKU not found in map
const DEFAULT_PRODUCT_IMAGE = '/UnisynHub/Assets/equicenta-card.png';

// Hidden per Heather's request while product info content is being finalized.
// Flip to `true` to re-enable the "View Product Info" buttons on every product card.
const SHOW_VIEW_PRODUCT_INFO = false;

// Phrases to render in bold within product descriptions
const BOLD_PHRASES = ['For Injury & Advanced Cases', 'For Maintenance & Longevity'];

// Render description text with specific phrases bolded
const renderDescriptionWithBold = (text) => {
  if (!text) return null;
  let remaining = text;
  const parts = [];
  let key = 0;
  while (remaining.length > 0) {
    let earliestIdx = -1;
    let earliestPhrase = null;
    for (const phrase of BOLD_PHRASES) {
      const idx = remaining.indexOf(phrase);
      if (idx !== -1 && (earliestIdx === -1 || idx < earliestIdx)) {
        earliestIdx = idx;
        earliestPhrase = phrase;
      }
    }
    if (earliestIdx === -1) { parts.push(<span key={key++}>{remaining}</span>); break; }
    if (earliestIdx > 0) parts.push(<span key={key++}>{remaining.slice(0, earliestIdx)}</span>);
    parts.push(<strong key={key++}>{earliestPhrase}</strong>);
    remaining = remaining.slice(earliestIdx + earliestPhrase.length);
  }
  return parts;
};

const PlaceOrderNew = ({ currentUser, onOrderSubmit }) => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems, setCartItems] = useCartStorage();
  const [pricingTiers, setPricingTiers] = useState({});

  // Track quantities per product
  const [quantities, setQuantities] = useState({});

  // Track expanded description state per product
  const [expandedDesc, setExpandedDesc] = useState({});

  // Get the customer's pricing tier group (if assigned by admin)
  const customerTierGroupId = currentUser?.profile?.pricingTierGroupId || null;

  useEffect(() => {
    setCartItems(prev => {
      const next = filterPurchasableCartItems(prev);
      return next.length === prev.length ? prev : next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve image: an admin-uploaded image (Product Management) wins; the
  // bundled SKU/name assets are only a fallback when no image was uploaded.
  const getProductImage = (product) => {
    if (product.images && product.images.length > 0 && product.images[0]) {
      return product.images[0];
    }
    const sku = product.sku;
    const mapped = sku ? PRODUCT_IMAGE_MAP[sku] : null;
    if (mapped) return mapped;
    const name = (product.name || '').toLowerCase();
    if (name.includes('maintain')) {
      return name.includes('3') || (product.volume || '').includes('3')
        ? '/UnisynHub/Assets/maintain-3ml-card.png'
        : '/UnisynHub/Assets/maintain-1.5ml-card.png';
    }
    return DEFAULT_PRODUCT_IMAGE;
  };

  const displayProducts = useMemo(() => {
    return products
      .map((product, index) => ({ product, index }))
      .sort((a, b) => {
        const aComingSoon = isComingSoonCartItem(a.product);
        const bComingSoon = isComingSoonCartItem(b.product);

        if (aComingSoon !== bComingSoon) {
          return aComingSoon ? 1 : -1;
        }

        return a.index - b.index;
      })
      .map(({ product }) => product);
  }, [products]);

  // Fetch products and their pricing tiers from the API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setProductsError(null);
        const response = await productAPI.getProducts({ organization: 'EPL', status: 'ACTIVE' });

        if (response.success && response.data && response.data.length > 0) {
          const eplProducts = response.data
            .filter(p => p.category === 'EQUINE')
            .map(p => ({
              ...p,
              productId: p.id,
              basePrice: Number(p.basePrice),
            }));

          setProducts(eplProducts);

          // Initialize quantities to 1 so Add to Cart is always active
          const initialQuantities = {};
          eplProducts.forEach(p => { initialQuantities[p.id] = 1; });
          setQuantities(initialQuantities);

          // Fetch pricing tiers for each product (with customer's tier group if assigned)
          const tierPromises = eplProducts.map(async (product) => {
            try {
              const tiersResponse = await productAPI.getPricingTiers(
                product.id, customerTierGroupId
              );
              if (tiersResponse.success && tiersResponse.data && tiersResponse.data.length > 0) {
                return { productId: product.id, tiers: tiersResponse.data };
              }
            } catch (err) {
              logger.error(`Error fetching pricing tiers for ${product.id}:`, err);
            }
            return null;
          });

          const tierResults = await Promise.all(tierPromises);
          const tiersMap = {};
          tierResults.forEach(result => {
            if (result) {
              tiersMap[result.productId] = result.tiers;
            }
          });
          setPricingTiers(tiersMap);
        } else {
          setProductsError('No products available. Please contact support.');
        }
      } catch (error) {
        logger.error('Error fetching products:', error);
        setProductsError('Unable to load products. Please try again.');
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, [customerTierGroupId]);

  const handleQuantityChange = (productId, change) => {
    setQuantities(prev => {
      const currentValue = prev[productId] || 0;
      const newValue = currentValue + change;
      return {
        ...prev,
        [productId]: Math.max(1, newValue)
      };
    });
  };

  const handleAddToCart = (product, overrideQuantity) => {
    if (!product || isComingSoonCartItem(product)) return;

    const quantity = overrideQuantity ?? quantities[product.id] ?? 0;

    if (!quantity || quantity < 1) return;

    const newItem = {
      name: product.name,
      price: product.basePrice || 0,
      basePrice: product.basePrice || 0,
      quantity: quantity,
      image: getProductImage(product),
      sku: product.sku,
      productId: product.productId || product.id || product.sku
    };

    // Existing SKUs are updated to the selected quantity shown on the card.
    setCartItems(prev => {
      const existingIndex = prev.findIndex(item => item.sku === product.sku);
      if (existingIndex >= 0) {
        return prev.map((item, idx) =>
          idx === existingIndex ? newItem : item
        );
      }
      return [...prev, newItem];
    });

    setCartOpen(true);

    // Reset quantity to 1
    setQuantities(prev => ({
      ...prev,
      [product.id]: 1
    }));
  };

  const handleQuickAdd = (product, tier) => {
    if (!product || !tier || isComingSoonCartItem(product)) return;

    const quantity = tier.minQuantity || 2;
    const basePrice = product.basePrice || 0;
    const tierPrice = Number(tier.finalPrice || tier.pricePerUnit) || basePrice;
    const discountPercent = Number(tier.discountPercent) || 0;

    const newItem = {
      name: product.name,
      price: tierPrice,
      basePrice,
      discountPercent,
      quantity,
      image: getProductImage(product),
      sku: product.sku,
      productId: product.productId || product.id || product.sku,
    };

    setCartItems(prev => {
      const existingIndex = prev.findIndex(item => item.sku === product.sku);
      if (existingIndex >= 0) {
        return prev.map((item, idx) =>
          idx === existingIndex
            ? { ...newItem, quantity: (Number(item.quantity) || 0) + quantity }
            : item
        );
      }
      return [...prev, newItem];
    });
    setCartOpen(true);
  };

  const handleUpdateCartQuantity = (itemIndex, newQuantity) => {
    if (newQuantity === '') return;

    const numQuantity = typeof newQuantity === 'number' ? newQuantity : parseInt(newQuantity, 10);

    if (isNaN(numQuantity) || numQuantity <= 0) {
      handleRemoveFromCart(itemIndex);
      return;
    }

    setCartItems(prev => prev.map((item, index) =>
      index === itemIndex ? { ...item, quantity: numQuantity } : item
    ));
  };

  const handleRemoveFromCart = (itemIndex) => {
    setCartItems(prev => prev.filter((_, index) => index !== itemIndex));
  };

  const handleCheckout = () => {
    const checkoutItems = filterPurchasableCartItems(cartItems);

    setCartOpen(false);
    setCartItems([]);

    if (checkoutItems.length === 0) return;

    navigate('/client/checkout', {
      state: { cartItems: checkoutItems }
    });
  };

  const formatPrice = (price) => {
    return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <Box
      sx={{
        pt: { xs: 7, md: 0 },
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      {/* Top Cart Summary Bar */}
      {cartItems.length > 0 && (
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: '#2c2c2c',
          color: 'white',
          py: { xs: 1.5, md: 1.5 },
          px: { xs: 2, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, md: 2 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M7 18C5.9 18 5.01 18.9 5.01 20S5.9 22 7 22 9 21.1 9 20 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.25 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.59 17.3 11.97L20.88 5H5.21L4.27 3H1V2M17 18C15.9 18 15.01 18.9 15.01 20S15.9 22 17 22 19 21.1 19 20 18.1 18 17 18Z" />
            </svg>
            <Typography sx={{ fontWeight: 600, fontSize: '14px', fontFamily: 'Poppins' }}>
              {cartItems.reduce((total, item) => total + item.quantity, 0)} item{cartItems.reduce((total, item) => total + item.quantity, 0) !== 1 ? 's' : ''} in cart
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' },
          }}>
            <Typography sx={{ fontWeight: 600, fontSize: '16px', fontFamily: 'Poppins' }}>
              ${cartItems.reduce((total, item) => total + (item.price * item.quantity), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setCartOpen(true)}
              sx={{
                bgcolor: 'white',
                color: '#2c2c2c',
                fontWeight: 500,
                fontSize: '13px',
                fontFamily: 'Poppins',
                px: 2,
                py: 0.5,
                borderRadius: '6px',
                textTransform: 'none',
                minWidth: { xs: 110, sm: 'auto' },
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              View Cart
            </Button>
            <Button
              variant="contained"
              onClick={handleCheckout}
              sx={{
                bgcolor: '#009c87',
                color: 'white',
                fontWeight: 500,
                fontSize: '13px',
                fontFamily: 'Poppins',
                px: 2,
                py: 0.5,
                borderRadius: '6px',
                textTransform: 'none',
                minWidth: { xs: 110, sm: 'auto' },
                '&:hover': { bgcolor: '#008574' },
              }}
            >
              Checkout
            </Button>
          </Box>
        </Box>
      )}

      {/* Page Header */}
      <Box sx={{ pt: { xs: 2, md: 0 }, pb: 2, px: { xs: 2, md: 0 } }}>
        <Typography sx={{
          fontSize: { xs: '20px', md: '24px' },
          fontFamily: 'Poppins',
          fontWeight: 700,
          color: '#1e1e1e',
          lineHeight: 1.2,
        }}>
          Shop Our Products
        </Typography>
        <Typography sx={{
          fontSize: '12px',
          fontFamily: 'Poppins',
          fontWeight: 500,
          color: '#000000',
          lineHeight: '100%',
          mt: 0.5,
        }}>
          Exclusive Partnership with Equine Performance Labs
        </Typography>
      </Box>

      {/* Loading State */}
      {productsLoading && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: '#009c87', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Poppins' }}>
            Loading products...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {productsError && !productsLoading && (
        <Box sx={{ py: 4, px: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{productsError}</Alert>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ bgcolor: '#2c2c2c', textTransform: 'none', fontFamily: 'Poppins' }}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* No Products State */}
      {!productsLoading && !productsError && products.length === 0 && (
        <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
          <Alert severity="info">No products available at this time. Please contact support.</Alert>
        </Box>
      )}

      {/* Products Grid */}
      {!productsLoading && !productsError && products.length > 0 && (
        <Box sx={{ px: { xs: 2, md: 0 }, pb: 4 }}>
          <Grid container spacing={{ xs: 2, md: 2.5 }}>
            {displayProducts.map((product) => {
              const tiers = (pricingTiers[product.id] || [])
                .filter(tier => tier.isActive !== false)
                .sort((a, b) => a.displayOrder - b.displayOrder || a.minQuantity - b.minQuantity);
              const qty = quantities[product.id] || 0;
              const isOutOfStock = product.availability?.inStock === false || product.availability?.quantity === 0;
              // CTM no longer offers tiered pricing — hide the 25+ Vials bulk row
              // for the equicenta CTM card. Maintain products keep it.
              const isMaintainProduct = isComingSoonCartItem(product);
              const isInCart = Boolean(product.sku) && cartItems.some(item => item.sku === product.sku);
              const isAddDisabled = qty < 1 || isOutOfStock || isMaintainProduct;
              // The crop/black-bg treatment below is tuned for the bundled CTM
              // packshot; admin-uploaded images must render neutrally instead.
              const hasCustomImage = Boolean(product.images && product.images.length > 0 && product.images[0]);
              const usePackshotCrop = !hasCustomImage && (product.sku?.startsWith('eCTM') || product.sku?.startsWith('EQC'));

              return (
                <Grid item xs={12} sm={6} lg={4} key={product.id}>
                  <Box sx={{
                    bgcolor: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '8px',
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    height: '100%',
                    minWidth: 0,
                    opacity: isOutOfStock ? 0.55 : 1,
                  }}>
                    {/* Coming Soon banner for maintain products */}
                    {isMaintainProduct && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                        <Chip
                          label="Coming Soon"
                          size="small"
                          sx={{
                            bgcolor: '#fef3c7',
                            color: '#92400e',
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                            fontSize: '12px',
                            letterSpacing: '0.5px',
                            border: '1px solid #fcd34d',
                            px: 1,
                          }}
                        />
                      </Box>
                    )}

                    {/* Product Image */}
                    <Box sx={{
                      height: { xs: 185, sm: 220, md: 247 },
                      border: '1px solid #d9d9d9',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      position: 'relative',
                      bgcolor: usePackshotCrop ? '#000' : '#fff',
                    }}>
                      <img
                        src={getProductImage(product)}
                        alt={product.name}
                        style={usePackshotCrop ? {
                          // Equicenta: original crop positioning, black bg fills the gaps
                          position: 'absolute',
                          width: '105%',
                          height: '179%',
                          top: '-67%',
                          left: '-2.4%',
                          objectFit: 'contain',
                        } : {
                          // Maintain products: contain the full image
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                      {isOutOfStock && (
                        <Box sx={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                        }}>
                          <Chip
                            label="Out of Stock"
                            size="small"
                            sx={{
                              bgcolor: '#fee2e2',
                              color: '#dc2626',
                              fontWeight: 700,
                              fontFamily: 'Poppins',
                              fontSize: '11px',
                              border: '1px solid #fca5a5',
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Body */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, minWidth: 0 }}>
                      {/* Avatar Block */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Box
                          component="img"
                          src="/UnisynHub/Assets/ep-avatar.png"
                          alt="EP"
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            border: '0.5px solid #cdcdcd',
                            flexShrink: 0,
                          }}
                        />
                        <Typography sx={{
                          fontSize: '12px',
                          fontFamily: 'Poppins',
                          fontWeight: 500,
                          color: '#1e1e1e',
                          lineHeight: 1.4,
                        }}>
                          Equine Performance Labs
                        </Typography>
                      </Box>

                      {/* Product Name */}
                      <Typography sx={{
                        fontSize: { xs: '22px', sm: '24px' },
                        fontFamily: 'Poppins',
                        fontWeight: 700,
                        color: '#1e1e1e',
                        lineHeight: 1.2,
                        letterSpacing: '-0.48px',
                        wordBreak: 'break-word',
                      }}>
                        {(product.name || '').replace(/\s*\(\d[^)]*\)/g, '').trim()}
                      </Typography>

                      {/* Description */}
                      <Box>
                        <Typography component="div" sx={{
                          fontSize: '14px',
                          fontFamily: 'Poppins',
                          fontWeight: 400,
                          color: '#303030',
                          lineHeight: 1.4,
                          ...(!expandedDesc[product.id] && {
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }),
                          minHeight: { xs: 'auto', sm: '39px' },
                        }}>
                          {renderDescriptionWithBold(product.description)}
                        </Typography>
                        {product.description && product.description.length > 80 && (
                          <Typography
                            component="span"
                            onClick={() => setExpandedDesc(prev => ({ ...prev, [product.id]: !prev[product.id] }))}
                            sx={{
                              fontSize: '14px',
                              fontFamily: 'Poppins',
                              fontWeight: 700,
                              color: '#303030',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              display: 'inline-block',
                              mt: '2px',
                            }}
                          >
                            {expandedDesc[product.id] ? 'Less' : 'More'}
                          </Typography>
                        )}
                      </Box>

                      {/* Volume Tag + Price Row */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 0 },
                      }}>
                        {product.volume && (
                          <Box sx={{
                            bgcolor: '#f5f5f5',
                            borderRadius: '8px',
                            px: 1,
                            py: 1,
                          }}>
                            <Typography sx={{
                              fontSize: '16px',
                              fontFamily: 'Poppins',
                              fontWeight: 400,
                              color: '#444',
                              lineHeight: 1,
                            }}>
                              {product.volume}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <Typography sx={{
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 700,
                            color: '#1e1e1e',
                            lineHeight: 1.4,
                          }}>
                            {formatPrice(product.basePrice)}
                          </Typography>
                          <Typography sx={{
                            fontSize: '14px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            color: '#303030',
                            lineHeight: 1.4,
                          }}>
                            /Vial Base Price
                          </Typography>
                        </Box>
                      </Box>

                      {/* Quantity + Add to Cart Row */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                      }}>
                        {/* Quantity Selector */}
                        <Box sx={{
                          border: '1px solid #e6e6e6',
                          borderRadius: '170px',
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'white',
                          width: { xs: '100%', sm: 'auto' },
                          pointerEvents: isOutOfStock || isMaintainProduct ? 'none' : 'auto',
                        }}>
                          <Box
                            onClick={() => handleQuantityChange(product.id, -1)}
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: '#f2f2f2',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#e5e5e5' },
                            }}
                          >
                            <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
                              <path d="M1 1H9" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </Box>

                          <TextField
                            type="number"
                            value={qty}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                setQuantities(prev => ({ ...prev, [product.id]: 1 }));
                              } else {
                                const numValue = parseInt(value, 10);
                                if (!isNaN(numValue)) {
                                  setQuantities(prev => ({ ...prev, [product.id]: Math.max(1, numValue) }));
                                }
                              }
                            }}
                            inputProps={{
                              style: {
                                textAlign: 'center',
                                padding: '4px 0',
                                fontSize: '16px',
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                color: '#1a1a1a',
                                width: '40px',
                                MozAppearance: 'textfield',
                              }
                            }}
                            sx={{
                              width: '40px',
                              '& .MuiOutlinedInput-root': {
                                height: '34px',
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: 'none' },
                                '&.Mui-focused fieldset': { border: 'none' },
                                '& input[type=number]': {
                                  MozAppearance: 'textfield',
                                  '&::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                  '&::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                },
                              },
                            }}
                          />

                          <Box
                            onClick={() => handleQuantityChange(product.id, 1)}
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: '#f2f2f2',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#e5e5e5' },
                            }}
                          >
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path d="M5 1V9M1 5H9" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </Box>
                        </Box>

                        {/* Add to Cart Button */}
                        <Button
                          onClick={() => handleAddToCart(product)}
                          disabled={isAddDisabled}
                          sx={{
                            width: { xs: '100%', sm: '134px' },
                            height: '50px',
                            borderRadius: '60px',
                            bgcolor: isAddDisabled ? '#d9d9d9' : '#2c2c2c',
                            color: isAddDisabled ? '#b3b3b3' : '#f5f5f5',
                            border: isAddDisabled ? '1px solid #b3b3b3' : '1px solid #2c2c2c',
                            fontSize: '14px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            textTransform: 'none',
                            '&:hover': {
                              bgcolor: isAddDisabled ? '#d9d9d9' : '#1a1a1a',
                            },
                            '&.Mui-disabled': {
                              bgcolor: '#d9d9d9',
                              color: '#b3b3b3',
                              border: '1px solid #b3b3b3',
                            },
                          }}
                        >
                          {isMaintainProduct ? 'Coming Soon' : isInCart ? 'Update Cart' : 'Add to Cart'}
                        </Button>
                      </Box>

                      {/* Volume Discounts section — tier ladder only appears when 2+ tiers exist,
                          but the 25+ Vials "Contact Us" row is always shown so clients can request bulk pricing. */}
                      <Divider sx={{ borderColor: '#e5e5e5' }} />

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {tiers.length >= 2 && (
                          <Typography sx={{
                            fontSize: '10px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            color: '#303030',
                            letterSpacing: '1.9px',
                            textAlign: 'center',
                            textTransform: 'uppercase',
                          }}>
                            Volume Discounts
                          </Typography>
                        )}

                        {/* Pricing Tiers — hidden when fewer than 2 tiers exist */}
                        {tiers.length >= 2 && tiers.map((tier, index) => {
                          const tierPrice = Number(tier.finalPrice || tier.pricePerUnit) || 0;
                          const discountPct = Number(tier.discountPercent) || 0;
                          const discountText = `${Math.round(discountPct)}% Discount`;
                          return (
                            <Box key={tier.id || index} sx={{
                              bgcolor: 'white',
                              border: '1px solid #f5f5f5',
                              borderRadius: '8px',
                              px: '10px',
                              py: 1,
                              display: 'flex',
                              alignItems: { xs: 'stretch', sm: 'center' },
                              justifyContent: 'space-between',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: 1,
                            }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{
                                  fontSize: '16px',
                                  fontFamily: 'Poppins',
                                  fontWeight: 700,
                                  color: '#1e1e1e',
                                  lineHeight: 1.4,
                                }}>
                                  {`${tier.tierName || `Tier ${index + 1}`} (${tier.minQuantity}${tier.maxQuantity ? `\u2013${tier.maxQuantity}` : '+'} Vials)`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                                  <Typography sx={{
                                    fontSize: '16px',
                                    fontFamily: 'Poppins',
                                    fontWeight: 700,
                                    color: '#1e1e1e',
                                    lineHeight: 1.4,
                                  }}>
                                    {formatPrice(tierPrice)}
                                  </Typography>
                                  <Typography sx={{
                                    fontSize: '14px',
                                    fontFamily: 'Poppins',
                                    fontWeight: 400,
                                    color: '#303030',
                                    lineHeight: 1.4,
                                  }}>
                                    /Vial ({discountText})
                                  </Typography>
                                </Box>
                              </Box>
                              <Button
                                onClick={() => handleQuickAdd(product, tier)}
                                disabled={isOutOfStock || isMaintainProduct}
                                sx={{
                                  bgcolor: isOutOfStock || isMaintainProduct ? '#d9d9d9' : '#2c2c2c',
                                  color: isOutOfStock || isMaintainProduct ? '#b3b3b3' : '#f5f5f5',
                                  border: isOutOfStock || isMaintainProduct ? '1px solid #b3b3b3' : '1px solid #2c2c2c',
                                  borderRadius: '8px',
                                  px: 1,
                                  py: 1,
                                  fontSize: '14px',
                                  fontFamily: 'Poppins',
                                  fontWeight: 400,
                                  textTransform: 'none',
                                  gap: 1,
                                  lineHeight: 1,
                                  width: { xs: '100%', sm: 'auto' },
                                  '&:hover': { bgcolor: '#1a1a1a' },
                                  '&.Mui-disabled': {
                                    bgcolor: '#d9d9d9',
                                    color: '#b3b3b3',
                                    border: '1px solid #b3b3b3',
                                  },
                                }}
                              >
                                <AddIcon sx={{ fontSize: 16 }} />
                                Quick Add
                              </Button>
                            </Box>
                          );
                        })}

                        {/* 25+ Vials Row — shown for maintain products only.
                            Hidden for CTM since CTM no longer has tiered pricing. */}
                        {isMaintainProduct && (
                          <Box sx={{
                            bgcolor: 'white',
                            border: '1px solid #f5f5f5',
                            borderRadius: '8px',
                            px: '10px',
                            py: 1,
                            display: 'flex',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                          }}>
                            <Typography sx={{
                              fontSize: '16px',
                              fontFamily: 'Poppins',
                              fontWeight: 700,
                              color: '#1e1e1e',
                              lineHeight: 1.4,
                            }}>
                              25+ Vials
                            </Typography>
                            <Button
                              onClick={() => window.open('mailto:info@equineperformancelabs.com')}
                              sx={{
                                border: '1px solid #e3e3e3',
                                borderRadius: '8px',
                                px: 1,
                                py: 1,
                                fontSize: '14px',
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                color: '#1e1e1e',
                                textTransform: 'none',
                                lineHeight: 1,
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': { bgcolor: '#f5f5f5' },
                              }}
                            >
                              Contact Us
                            </Button>
                          </Box>
                        )}

                        {/* View Product Info — hidden per Heather's request while
                            product info content is being finalized. Re-enable by
                            flipping SHOW_VIEW_PRODUCT_INFO to true. */}
                        {SHOW_VIEW_PRODUCT_INFO && (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                              onClick={() => {
                                const sku = product.sku || '';
                                const name = (product.name || '').toLowerCase();
                                const vol = (product.volume || '').toLowerCase();
                                let route = 'equicent';
                                if (sku === 'MNT-001' || (name.includes('maintain') && (name.includes('3') || vol.includes('3')))) {
                                  route = 'maintain-3ml';
                                } else if (sku === 'MNT-002' || name.includes('maintain')) {
                                  route = 'maintain-1.5ml';
                                }
                                navigate(`/client/product/${route}`);
                              }}
                              sx={{
                                border: '1px solid #e3e3e3',
                                borderRadius: '8px',
                                px: 1,
                                py: 1,
                                fontSize: '14px',
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                color: '#1e1e1e',
                                textTransform: 'none',
                                lineHeight: 1,
                                '&:hover': { bgcolor: '#f5f5f5' },
                              }}
                            >
                              View Product Info
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Shopping Cart Sidebar */}
      <ShoppingCart
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={handleUpdateCartQuantity}
        onRemoveItem={handleRemoveFromCart}
        onCheckout={handleCheckout}
      />
    </Box>
  );
};

export default PlaceOrderNew;
